'use client';

import React, { useEffect, useRef, useState } from 'react';

interface Service {
  number: string;
  title: string;
  description: string;
  deliverables: string[];
  color: string;
}

const services: Service[] = [
  {
    number: '01',
    title: 'Digital Marketing Strategy',
    description:
      'End-to-end marketing strategies built on data and aligned with your business goals. From market research to channel selection and KPI frameworks.',
    deliverables: ['Market & competitor analysis', 'Channel strategy', 'KPI framework', 'Campaign roadmap'],
    color: '#7c3aed',
  },
  {
    number: '02',
    title: 'Content Creation & Campaigns',
    description:
      'Compelling content that tells your story and drives action. Blog posts, social copy, email campaigns, and multimedia content tailored to your audience.',
    deliverables: ['Editorial calendar', 'Blog & web copy', 'Email campaigns', 'Campaign creatives'],
    color: '#06b6d4',
  },
  {
    number: '03',
    title: 'Social Media Management',
    description:
      'Strategic social media presence across platforms. Community management, content scheduling, engagement strategy, and performance tracking.',
    deliverables: ['Platform strategy', 'Content scheduling', 'Community management', 'Monthly reporting'],
    color: '#3a6b9e',
  },
  {
    number: '04',
    title: 'SEO Optimization',
    description:
      'Improve your organic visibility with technical SEO, keyword strategy, and content optimization. Built for sustainable, long-term growth.',
    deliverables: ['SEO audit', 'Keyword research', 'On-page optimization', 'Performance tracking'],
    color: '#7c3aed',
  },
  {
    number: '05',
    title: 'Communication & Brand Positioning',
    description:
      'Define and communicate your brand with clarity and confidence. Messaging frameworks, brand voice guidelines, and stakeholder communication.',
    deliverables: ['Brand messaging', 'Tone of voice guide', 'Positioning strategy', 'PR support'],
    color: '#d4a843',
  },
];

export default function Services() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="bg-white py-24 md:py-32" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-5 md:px-16 lg:px-24">
        {/* Header */}
        <div className="mb-16">
          <span className="section-label animate-on-scroll">04 — Services</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-4">
            <h2
              className="section-title animate-on-scroll"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)' }}
            >
              What I
              <br />
              <span className="text-primary">Offer</span>
            </h2>
            <p
              className="font-sans text-dark opacity-70 max-w-sm animate-on-scroll"
              style={{ fontSize: '0.95rem', lineHeight: '1.7' }}
            >
              Flexible services for agencies, startups, and corporate teams looking for
              strategic marketing support.
            </p>
          </div>
        </div>

        {/* Services list */}
        <div className="space-y-0">
          {services.map((service, index) => (
            <ServiceItem key={service.number} service={service} index={index} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 pt-8 border-t border-secondary-dark flex flex-col md:flex-row items-start md:items-center justify-between gap-6 animate-on-scroll">
          <div>
            <p
              className="font-condensed font-bold uppercase text-dark"
              style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.8rem)' }}
            >
              Ready to work together?
            </p>
            <p className="font-sans text-dark opacity-60 text-sm mt-1">
              Let&rsquo;s discuss your project and find the right approach.
            </p>
          </div>
          <a href="#contact" className="btn-primary">
            Get In Touch
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

function ServiceItem({ service, index }: { service: Service; index: number }) {
  const [expanded, setExpanded] = React.useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, index * 100);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (itemRef.current) observer.observe(itemRef.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={itemRef}
      className="animate-on-scroll border-t border-secondary-dark group cursor-pointer"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between py-6 md:py-8 gap-4">
        <div className="flex items-center gap-4 md:gap-10 min-w-0">
          <span
            className="font-condensed font-bold text-xs tracking-widest flex-shrink-0"
            style={{ color: service.color }}
          >
            {service.number}
          </span>
          <h3
            className="font-condensed font-bold uppercase text-dark group-hover:text-primary transition-colors duration-300"
            style={{ fontSize: 'clamp(1rem, 2.5vw, 1.8rem)' }}
          >
            {service.title}
          </h3>
        </div>
        <div
          className="flex-shrink-0 w-8 h-8 border border-dark flex items-center justify-center transition-all duration-300 group-hover:bg-dark group-hover:text-light-warm"
          style={{
            transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease, background-color 0.3s ease',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {/* Expanded content */}
      <div
        className="overflow-hidden transition-all duration-500"
        style={{ maxHeight: expanded ? '300px' : '0px' }}
      >
        <div className="pb-8 grid grid-cols-1 md:grid-cols-2 gap-8 pl-0 md:pl-16">
          <p
            className="font-sans text-dark opacity-70 leading-relaxed"
            style={{ fontSize: '0.95rem' }}
          >
            {service.description}
          </p>
          <div>
            <p
              className="font-condensed font-bold uppercase tracking-widest text-xs mb-3"
              style={{ color: service.color }}
            >
              Deliverables
            </p>
            <ul className="space-y-2">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-center gap-2">
                  <span
                    className="w-1 h-1 rounded-full flex-shrink-0"
                    style={{ backgroundColor: service.color }}
                  />
                  <span className="font-sans text-dark opacity-70 text-sm">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}